import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Trophy,
  Upload,
  Download,
  Plus,
  Trash2,
  RefreshCw,
  AlertCircle,
  Check,
  X,
  Edit2,
  FileSpreadsheet,
  ChevronDown,
  ChevronRight,
  Users,
  Calendar,
  Hash,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { useTheme } from '../../context/ThemeContext';
import CustomSelect from '../../components/common/CustomSelect';
import {
  getStandings,
  savePlayer,
  deletePlayer,
  OOM_CATEGORIES,
  API_BASE_URL,
} from '../../services/oomService';
import { parseOOMExcel, detectYear, importOOMData } from '../../services/oomExcelParser';

// Editable cell component
const EditableCell = ({ value, onSave, playerId, eventId, isDarkMode }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value ?? '');
  const inputRef = useRef(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = () => {
    const numValue = tempValue === '' ? null : parseInt(tempValue, 10);
    if (numValue !== value) {
      onSave(playerId, eventId, numValue);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setTempValue(value ?? '');
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="number"
        value={tempValue}
        onChange={(e) => setTempValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        className={`w-12 text-center text-xs rounded border px-1 py-0.5 ${
          isDarkMode
            ? 'bg-fsgu-gray-700 border-fsgu-gray-600 text-fsgu-silver-100'
            : 'bg-white border-fsgu-silver-300 text-fsgu-silver-900'
        }`}
      />
    );
  }

  return (
    <span
      onClick={() => setIsEditing(true)}
      className={`cursor-pointer px-1 py-0.5 rounded hover:ring-2 ring-fsgu-blue-500/50 min-w-[2rem] inline-block text-center ${
        value ? '' : isDarkMode ? 'text-fsgu-silver-600' : 'text-fsgu-silver-400'
      }`}
      title="Click to edit"
    >
      {value ?? '-'}
    </span>
  );
};

const AdminOOM = () => {
  const { isDarkMode } = useTheme();

  // Filters
  const [category, setCategory] = useState('Mens_Open');
  const [year, setYear] = useState(new Date().getFullYear());

  // Data
  const [standings, setStandings] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [savingCell, setSavingCell] = useState(null);

  // Edit mode for player info
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  // Add new row
  const [showAddRow, setShowAddRow] = useState(false);
  const [newRow, setNewRow] = useState({
    player_name: '',
    club: '',
  });

  // Import modal
  const [showImport, setShowImport] = useState(false);
  const [importParsedData, setImportParsedData] = useState(null);
  const [importYear, setImportYear] = useState(new Date().getFullYear());
  const [importFileName, setImportFileName] = useState('');
  const [selectedSheets, setSelectedSheets] = useState({});
  const [expandedSheet, setExpandedSheet] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  // Year options
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 5 }, (_, i) => ({
    value: currentYear - i,
    label: String(currentYear - i),
  }));

  // Load standings when category/year changes
  useEffect(() => {
    loadStandings();
  }, [category, year]);

  const loadStandings = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getStandings(category, year);
      setStandings(response.standings || []);
      setEvents(response.events || []);
    } catch (err) {
      setError(err.message);
      setStandings([]);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (msg, isError = false) => {
    if (isError) {
      setError(msg);
      setSuccess(null);
    } else {
      setSuccess(msg);
      setError(null);
    }
    setTimeout(() => {
      setError(null);
      setSuccess(null);
    }, 4000);
  };

  // Helper to get event score key (matches API format)
  const getEventScoreKey = useCallback((event) => {
    if (event.is_bonus_event) {
      return `bp_${(event.event_number || 101) - 100}`;
    }
    return `e_${event.event_number || event.display_order}`;
  }, []);

  // Save individual event score
  const handleSaveScore = useCallback(async (playerId, eventId, scoreKey, points) => {
    setSavingCell({ playerId, eventId });
    try {
      const response = await fetch(`${API_BASE_URL}/oom-scores.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          player_id: playerId,
          event_id: eventId,
          points: points ?? 0,
        }),
      });

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.message || 'Failed to save score');
      }

      // Update local state
      setStandings((prev) =>
        prev.map((row) => {
          if (row.player_id === playerId) {
            return {
              ...row,
              event_scores: { ...row.event_scores, [scoreKey]: points },
            };
          }
          return row;
        })
      );

      showMessage('Score saved');
    } catch (err) {
      showMessage(err.message, true);
    } finally {
      setSavingCell(null);
    }
  }, []);

  // Add new player
  const handleAddRow = async () => {
    if (!newRow.player_name.trim()) {
      showMessage('Player name is required', true);
      return;
    }

    try {
      // Need season_id - get from current standings response or create
      const seasonResponse = await getStandings(category, year);
      const seasonId = seasonResponse.season?.id;

      if (!seasonId) {
        showMessage('No season found for this category/year. Import data first.', true);
        return;
      }

      await savePlayer({
        season_id: seasonId,
        name: newRow.player_name.trim(),
        club_name: newRow.club.trim() || null,
      });

      setNewRow({ player_name: '', club: '' });
      setShowAddRow(false);
      showMessage('Player added');
      loadStandings();
    } catch (err) {
      showMessage(err.message, true);
    }
  };

  // Edit row
  const startEdit = (row) => {
    setEditingId(row.player_id);
    setEditForm({
      player_name: row.player_name,
      club: row.club_name || '',
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const saveEdit = async () => {
    if (!editForm.player_name.trim()) {
      showMessage('Player name is required', true);
      return;
    }

    try {
      await savePlayer({
        id: editingId,
        name: editForm.player_name.trim(),
        club_name: editForm.club.trim() || null,
      });

      setEditingId(null);
      setEditForm({});
      showMessage('Changes saved');
      loadStandings();
    } catch (err) {
      showMessage(err.message, true);
    }
  };

  // Delete row
  const handleDelete = async (id, playerName) => {
    if (!window.confirm(`Delete ${playerName} from standings?`)) return;

    try {
      await deletePlayer(id);
      showMessage('Player removed');
      loadStandings();
    } catch (err) {
      showMessage(err.message, true);
    }
  };

  // Sort events: regular first (by event_number), then bonus
  const sortedEvents = useMemo(() => {
    return [...events].sort((a, b) => {
      if (a.is_bonus_event !== b.is_bonus_event) {
        return a.is_bonus_event ? 1 : -1;
      }
      return (a.event_number || a.display_order || 0) - (b.event_number || b.display_order || 0);
    });
  }, [events]);

  // Handle Excel file selection
  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImportFileName(file.name);
    setImportParsedData(null);
    setSelectedSheets({});

    try {
      const parsed = await parseOOMExcel(file);

      if (!parsed.success || parsed.sheets.length === 0) {
        showMessage('No valid data found in file', true);
        return;
      }

      setImportParsedData(parsed);

      const detectedYear = detectYear(file.name, parsed.sheets);
      setImportYear(detectedYear);

      const selected = {};
      parsed.sheets.forEach((sheet) => {
        selected[sheet.name] = true;
      });
      setSelectedSheets(selected);

      if (parsed.sheets.length > 0) {
        setExpandedSheet(parsed.sheets[0].name);
      }

      showMessage(
        `Found ${parsed.sheets.length} divisions with ${parsed.sheets.reduce(
          (acc, s) => acc + s.rows.length,
          0
        )} total players`
      );
    } catch (err) {
      showMessage('Error reading file: ' + err.message, true);
      setImportParsedData(null);
    }
  };

  // Import data to database
  const handleImport = async () => {
    if (!importParsedData || importParsedData.sheets.length === 0) {
      showMessage('No data to import. Please select an Excel file first.', true);
      return;
    }

    const sheetsToImport = importParsedData.sheets.filter((s) => selectedSheets[s.name]);

    if (sheetsToImport.length === 0) {
      showMessage('Please select at least one division to import', true);
      return;
    }

    const totalPlayers = sheetsToImport.reduce((acc, s) => acc + s.rows.length, 0);

    if (
      !window.confirm(
        `This will import ${sheetsToImport.length} division(s) with ${totalPlayers} players for ${importYear}.\n\nExisting data for these divisions will be replaced. Continue?`
      )
    ) {
      return;
    }

    try {
      setImporting(true);

      const result = await importOOMData(API_BASE_URL, importYear, sheetsToImport, true);

      const successCount = result.results.filter((r) => r.status === 'success').length;
      const totalImported = result.results.reduce((acc, r) => acc + (r.players_imported || 0), 0);

      setShowImport(false);
      setImportParsedData(null);
      setImportFileName('');
      setSelectedSheets({});
      if (fileInputRef.current) fileInputRef.current.value = '';

      showMessage(`Successfully imported ${successCount} division(s) with ${totalImported} players`);

      loadStandings();
    } catch (err) {
      showMessage(err.message, true);
    } finally {
      setImporting(false);
    }
  };

  // Export to Excel
  const handleExport = () => {
    if (standings.length === 0) {
      showMessage('No data to export', true);
      return;
    }

    const headers = ['Position', 'Player Name', 'Club'];

    sortedEvents.forEach((e) => {
      headers.push(e.is_bonus_event ? `BP${(e.event_number || 101) - 100}` : `E${e.event_number}`);
    });

    headers.push('Best 10 Points', 'OOM Total', 'Events');

    const exportData = [headers];

    standings.forEach((s) => {
      const row = [s.rank, s.player_name, s.club_name || ''];

      sortedEvents.forEach((e) => {
        const key = getEventScoreKey(e);
        row.push(s.event_scores?.[key] ?? '');
      });

      row.push(s.best_10_points || '', s.total_points || '', s.events_played || 0);
      exportData.push(row);
    });

    const ws = XLSX.utils.aoa_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `${getCategoryLabel(category)} ${year}`);
    XLSX.writeFile(wb, `OOM_${category}_${year}.xlsx`);
  };

  const getCategoryLabel = (value) => {
    const cat = OOM_CATEGORIES.find((c) => c.value === value);
    return cat ? cat.label : value;
  };

  const toggleSheetSelection = (sheetName) => {
    setSelectedSheets((prev) => ({
      ...prev,
      [sheetName]: !prev[sheetName],
    }));
  };

  const closeImportModal = () => {
    setShowImport(false);
    setImportParsedData(null);
    setImportFileName('');
    setSelectedSheets({});
    setExpandedSheet(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const baseColumns = 4;
  const eventColumns = sortedEvents.length;
  const totalColumns = baseColumns + eventColumns + 2; // +2 for Total and Best 10

  return (
    <div className="min-h-screen">
      {/* Sticky Header */}
      <div
        className={`sticky top-0 z-30 border-b backdrop-blur-md ${
          isDarkMode
            ? 'bg-fsgu-gray-900/95 border-fsgu-gray-700'
            : 'bg-fsgu-white/95 border-fsgu-silver-200'
        }`}
      >
        <div className="px-4 sm:px-6 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div
                className={`p-2 rounded-lg ${
                  isDarkMode ? 'bg-fsgu-green-500/20' : 'bg-fsgu-green-500/10'
                }`}
              >
                <Trophy
                  className={`w-6 h-6 ${
                    isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-600'
                  }`}
                />
              </div>
              <div>
                <h1
                  className={`text-2xl sm:text-3xl font-bold ${
                    isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
                  }`}
                >
                  Order of Merit
                </h1>
                <p
                  className={`text-sm ${
                    isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                  }`}
                >
                  Click any score cell to edit • Auto-saves on blur
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowImport(true)}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isDarkMode
                    ? 'bg-fsgu-gray-800 text-fsgu-silver-300 hover:bg-fsgu-gray-700'
                    : 'bg-fsgu-silver-100 text-fsgu-silver-700 hover:bg-fsgu-silver-200'
                }`}
              >
                <Upload className="w-4 h-4" />
                <span className="hidden sm:inline">Import</span>
              </button>

              <button
                onClick={handleExport}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isDarkMode
                    ? 'bg-fsgu-gray-800 text-fsgu-silver-300 hover:bg-fsgu-gray-700'
                    : 'bg-fsgu-silver-100 text-fsgu-silver-700 hover:bg-fsgu-silver-200'
                }`}
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">Export</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="px-4 sm:px-6 py-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="w-full sm:w-64">
            <label
              className={`block text-sm font-medium mb-1 ${
                isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
              }`}
            >
              Category
            </label>
            <CustomSelect
              name="category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              options={OOM_CATEGORIES}
              placeholder="Select category"
            />
          </div>

          <div className="w-full sm:w-40">
            <label
              className={`block text-sm font-medium mb-1 ${
                isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
              }`}
            >
              Year
            </label>
            <CustomSelect
              name="year"
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value))}
              options={yearOptions}
              placeholder="Select year"
            />
          </div>

          <div className="flex-1" />

          <div className="flex items-end">
            <button
              onClick={() => setShowAddRow(true)}
              className="btn btn-primary inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Player
            </button>
          </div>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="px-4 sm:px-6 pb-4">
          <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 text-red-500">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        </div>
      )}

      {success && (
        <div className="px-4 sm:px-6 pb-4">
          <div className="flex items-center gap-2 p-3 rounded-lg bg-green-500/10 text-green-500">
            <Check className="w-5 h-5 flex-shrink-0" />
            <span>{success}</span>
          </div>
        </div>
      )}

      {/* Standings Table */}
      <div className="px-4 sm:px-6 pb-6">
        <div
          className={`rounded-xl border overflow-hidden ${
            isDarkMode ? 'bg-fsgu-gray-800 border-fsgu-gray-700' : 'bg-white border-fsgu-silver-200'
          }`}
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr
                  className={`border-b ${
                    isDarkMode
                      ? 'bg-fsgu-gray-900 border-fsgu-gray-700'
                      : 'bg-fsgu-silver-50 border-fsgu-silver-200'
                  }`}
                >
                  <th
                    className={`px-3 py-3 text-center font-medium sticky left-0 z-10 ${
                      isDarkMode
                        ? 'bg-fsgu-gray-900 text-fsgu-silver-400'
                        : 'bg-fsgu-silver-50 text-fsgu-silver-600'
                    }`}
                    style={{ minWidth: '50px' }}
                  >
                    #
                  </th>
                  <th
                    className={`px-3 py-3 text-left font-medium ${
                      isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                    }`}
                    style={{ minWidth: '150px' }}
                  >
                    Player
                  </th>
                  <th
                    className={`px-3 py-3 text-left font-medium ${
                      isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                    }`}
                    style={{ minWidth: '120px' }}
                  >
                    Club
                  </th>
                  {sortedEvents.map((event) => (
                    <th
                      key={event.id}
                      className={`px-2 py-3 text-center font-medium ${
                        event.is_bonus_event
                          ? isDarkMode
                            ? 'text-amber-400 bg-amber-500/10'
                            : 'text-amber-600 bg-amber-500/5'
                          : isDarkMode
                          ? 'text-fsgu-silver-400'
                          : 'text-fsgu-silver-600'
                      }`}
                      style={{ minWidth: '50px' }}
                      title={event.name || `Event ${event.event_number}`}
                    >
                      {event.is_bonus_event
                        ? `BP${(event.event_number || 101) - 100}`
                        : `E${event.event_number || event.display_order}`}
                    </th>
                  ))}
                  <th
                    className={`px-3 py-3 text-center font-medium ${
                      isDarkMode
                        ? 'text-fsgu-green-400 bg-fsgu-green-500/10'
                        : 'text-fsgu-green-600 bg-fsgu-green-500/5'
                    }`}
                    style={{ minWidth: '70px' }}
                  >
                    Total
                  </th>
                  <th
                    className={`px-3 py-3 text-center font-medium ${
                      isDarkMode
                        ? 'text-amber-400 bg-amber-500/10'
                        : 'text-amber-600 bg-amber-500/5'
                    }`}
                    style={{ minWidth: '70px' }}
                    title="10 Best FS Rounds + Bonus Points"
                  >
                    Best 10
                  </th>
                  <th
                    className={`px-3 py-3 text-right font-medium ${
                      isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                    }`}
                    style={{ minWidth: '80px' }}
                  >
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody>
                {showAddRow && (
                  <tr
                    className={`border-b ${
                      isDarkMode
                        ? 'bg-fsgu-green-500/10 border-fsgu-gray-700'
                        : 'bg-fsgu-green-500/5 border-fsgu-silver-200'
                    }`}
                  >
                    <td className="px-3 py-2 text-center">
                      <Plus className="w-4 h-4 mx-auto text-fsgu-green-500" />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={newRow.player_name}
                        onChange={(e) => setNewRow({ ...newRow, player_name: e.target.value })}
                        placeholder="Player name"
                        className="input w-full text-sm"
                        autoFocus
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={newRow.club}
                        onChange={(e) => setNewRow({ ...newRow, club: e.target.value })}
                        placeholder="Club"
                        className="input w-full text-sm"
                      />
                    </td>
                    {sortedEvents.map((event) => (
                      <td key={event.id} className="px-2 py-2 text-center text-fsgu-silver-500">
                        -
                      </td>
                    ))}
                    <td className="px-3 py-2 text-center text-fsgu-silver-500">-</td>
                    <td className="px-3 py-2 text-center text-fsgu-silver-500">-</td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={handleAddRow}
                          className="p-1.5 rounded-lg bg-fsgu-green-500 text-white hover:bg-fsgu-green-600"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setShowAddRow(false);
                            setNewRow({ player_name: '', club: '' });
                          }}
                          className={`p-1.5 rounded-lg ${
                            isDarkMode
                              ? 'bg-fsgu-gray-700 text-fsgu-silver-400 hover:bg-fsgu-gray-600'
                              : 'bg-fsgu-silver-200 text-fsgu-silver-600 hover:bg-fsgu-silver-300'
                          }`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                )}

                {loading && standings.length === 0 && (
                  <tr>
                    <td colSpan={totalColumns + 1} className="px-4 py-12 text-center">
                      <RefreshCw
                        className={`w-8 h-8 mx-auto mb-3 animate-spin ${
                          isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-400'
                        }`}
                      />
                      <p className={isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'}>
                        Loading standings...
                      </p>
                    </td>
                  </tr>
                )}

                {!loading && standings.length === 0 && (
                  <tr>
                    <td colSpan={totalColumns + 1} className="px-4 py-12 text-center">
                      <Trophy
                        className={`w-12 h-12 mx-auto mb-3 ${
                          isDarkMode ? 'text-fsgu-silver-600' : 'text-fsgu-silver-300'
                        }`}
                      />
                      <p
                        className={`text-lg font-medium mb-1 ${
                          isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                        }`}
                      >
                        No standings yet
                      </p>
                      <p className={isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'}>
                        Import from FSGU Excel or add players manually
                      </p>
                    </td>
                  </tr>
                )}

                {standings.map((row, index) => {
                  const rowId = row.player_id;
                  const rowClub = row.club_name || '';
                  const position = row.rank || index + 1;

                  return (
                    <tr
                      key={rowId || index}
                      className={`border-b last:border-b-0 ${
                        isDarkMode
                          ? 'border-fsgu-gray-700 hover:bg-fsgu-gray-700/50'
                          : 'border-fsgu-silver-100 hover:bg-fsgu-silver-50'
                      } ${
                        editingId === rowId
                          ? isDarkMode
                            ? 'bg-fsgu-blue-500/10'
                            : 'bg-fsgu-blue-500/5'
                          : ''
                      }`}
                    >
                      <td
                        className={`px-3 py-2 text-center sticky left-0 z-10 ${
                          isDarkMode ? 'bg-fsgu-gray-800' : 'bg-white'
                        }`}
                      >
                        <span
                          className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${
                            position === 1
                              ? 'bg-yellow-500/20 text-yellow-500'
                              : position === 2
                              ? 'bg-gray-400/20 text-gray-400'
                              : position === 3
                              ? 'bg-amber-600/20 text-amber-600'
                              : isDarkMode
                              ? 'bg-fsgu-gray-700 text-fsgu-silver-400'
                              : 'bg-fsgu-silver-100 text-fsgu-silver-600'
                          }`}
                        >
                          {position}
                        </span>
                      </td>

                      <td className="px-3 py-2">
                        {editingId === rowId ? (
                          <input
                            type="text"
                            value={editForm.player_name}
                            onChange={(e) =>
                              setEditForm({ ...editForm, player_name: e.target.value })
                            }
                            className="input w-full text-sm"
                          />
                        ) : (
                          <span
                            className={`font-medium ${
                              isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
                            }`}
                          >
                            {row.player_name}
                          </span>
                        )}
                      </td>

                      <td className="px-3 py-2">
                        {editingId === rowId ? (
                          <input
                            type="text"
                            value={editForm.club}
                            onChange={(e) => setEditForm({ ...editForm, club: e.target.value })}
                            className="input w-full text-sm"
                          />
                        ) : (
                          <span
                            className={isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'}
                          >
                            {rowClub || '-'}
                          </span>
                        )}
                      </td>

                      {sortedEvents.map((event) => {
                        const scoreKey = getEventScoreKey(event);
                        const score = row.event_scores?.[scoreKey];
                        const isSaving =
                          savingCell?.playerId === row.player_id &&
                          savingCell?.eventId === event.id;

                        return (
                          <td
                            key={event.id}
                            className={`px-2 py-2 text-center text-xs ${
                              event.is_bonus_event
                                ? isDarkMode
                                  ? 'bg-amber-500/5'
                                  : 'bg-amber-500/5'
                                : ''
                            }`}
                          >
                            {isSaving ? (
                              <RefreshCw className="w-3 h-3 mx-auto animate-spin text-fsgu-blue-500" />
                            ) : (
                              <EditableCell
                                value={score}
                                onSave={(playerId, eventId, points) =>
                                  handleSaveScore(playerId, eventId, scoreKey, points)
                                }
                                playerId={row.player_id}
                                eventId={event.id}
                                isDarkMode={isDarkMode}
                              />
                            )}
                          </td>
                        );
                      })}

                      <td
                        className={`px-3 py-2 text-center font-semibold ${
                          isDarkMode
                            ? 'text-fsgu-green-400 bg-fsgu-green-500/5'
                            : 'text-fsgu-green-600 bg-fsgu-green-500/5'
                        }`}
                      >
                        {row.total_points}
                      </td>

                      {/* Best 10 */}
                      <td
                        className={`px-3 py-2 text-center font-semibold ${
                          isDarkMode
                            ? 'text-amber-400 bg-amber-500/5'
                            : 'text-amber-600 bg-amber-500/5'
                        }`}
                      >
                        {row.best_10_points || '-'}
                      </td>

                      <td className="px-3 py-2 text-right">
                        {editingId === rowId ? (
                          <div className="flex justify-end gap-1">
                            <button
                              onClick={saveEdit}
                              className="p-1.5 rounded-lg bg-fsgu-green-500 text-white hover:bg-fsgu-green-600"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              onClick={cancelEdit}
                              className={`p-1.5 rounded-lg ${
                                isDarkMode
                                  ? 'bg-fsgu-gray-700 text-fsgu-silver-400 hover:bg-fsgu-gray-600'
                                  : 'bg-fsgu-silver-200 text-fsgu-silver-600 hover:bg-fsgu-silver-300'
                              }`}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex justify-end gap-1">
                            <button
                              onClick={() => startEdit(row)}
                              className={`p-1.5 rounded-lg transition-colors ${
                                isDarkMode
                                  ? 'text-fsgu-silver-400 hover:bg-fsgu-gray-700 hover:text-fsgu-silver-200'
                                  : 'text-fsgu-silver-500 hover:bg-fsgu-silver-100 hover:text-fsgu-silver-700'
                              }`}
                              title="Edit player info"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(rowId, row.player_name)}
                              className={`p-1.5 rounded-lg transition-colors ${
                                isDarkMode
                                  ? 'text-fsgu-silver-400 hover:bg-red-500/20 hover:text-red-400'
                                  : 'text-fsgu-silver-500 hover:bg-red-500/10 hover:text-red-500'
                              }`}
                              title="Delete player"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {standings.length > 0 && (
          <div
            className={`mt-4 text-sm ${
              isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
            }`}
          >
            {standings.length} players • {getCategoryLabel(category)} {year}
            {sortedEvents.length > 0 && (
              <>
                {' '}
                • {sortedEvents.filter((e) => !e.is_bonus_event).length} events
                {sortedEvents.filter((e) => e.is_bonus_event).length > 0 &&
                  ` + ${sortedEvents.filter((e) => e.is_bonus_event).length} bonus`}
              </>
            )}
          </div>
        )}
      </div>

      {/* Import Modal */}
      {showImport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div
            className={`w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-xl shadow-xl flex flex-col ${
              isDarkMode ? 'bg-fsgu-gray-800' : 'bg-white'
            }`}
          >
            <div
              className={`flex items-center justify-between px-6 py-4 border-b flex-shrink-0 ${
                isDarkMode ? 'border-fsgu-gray-700' : 'border-fsgu-silver-200'
              }`}
            >
              <h2
                className={`text-xl font-bold ${
                  isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
                }`}
              >
                Import FSGU OOM Excel
              </h2>
              <button
                onClick={closeImportModal}
                className={`p-2 rounded-lg ${
                  isDarkMode
                    ? 'text-fsgu-silver-400 hover:bg-fsgu-gray-700'
                    : 'text-fsgu-silver-500 hover:bg-fsgu-silver-100'
                }`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1">
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className={`p-6 rounded-lg border-2 border-dashed cursor-pointer transition-colors ${
                    isDarkMode
                      ? 'border-fsgu-gray-600 hover:border-fsgu-green-500/50 bg-fsgu-gray-900'
                      : 'border-fsgu-silver-300 hover:border-fsgu-green-500/50 bg-fsgu-silver-50'
                  }`}
                >
                  <div className="text-center">
                    <FileSpreadsheet
                      className={`w-10 h-10 mx-auto mb-2 ${
                        importFileName
                          ? isDarkMode
                            ? 'text-fsgu-green-400'
                            : 'text-fsgu-green-600'
                          : isDarkMode
                          ? 'text-fsgu-silver-500'
                          : 'text-fsgu-silver-400'
                      }`}
                    />
                    {importFileName ? (
                      <p
                        className={`font-medium ${
                          isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-600'
                        }`}
                      >
                        {importFileName}
                      </p>
                    ) : (
                      <p className={isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'}>
                        Click to select FSGU OOM Excel file (.xlsx)
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {importParsedData && (
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Calendar
                      className={`w-4 h-4 ${
                        isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                      }`}
                    />
                    <label
                      className={`text-sm font-medium ${
                        isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                      }`}
                    >
                      Import Year:
                    </label>
                  </div>
                  <select
                    value={importYear}
                    onChange={(e) => setImportYear(parseInt(e.target.value))}
                    className={`px-3 py-1.5 rounded-lg border text-sm ${
                      isDarkMode
                        ? 'bg-fsgu-gray-700 border-fsgu-gray-600 text-fsgu-silver-200'
                        : 'bg-white border-fsgu-silver-300 text-fsgu-silver-800'
                    }`}
                  >
                    {yearOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {importParsedData && importParsedData.sheets.length > 0 && (
                <div
                  className={`rounded-lg border ${
                    isDarkMode ? 'border-fsgu-gray-700' : 'border-fsgu-silver-200'
                  }`}
                >
                  <div
                    className={`px-4 py-3 border-b ${
                      isDarkMode
                        ? 'border-fsgu-gray-700 bg-fsgu-gray-900'
                        : 'border-fsgu-silver-200 bg-fsgu-silver-50'
                    }`}
                  >
                    <p
                      className={`font-medium ${
                        isDarkMode ? 'text-fsgu-silver-200' : 'text-fsgu-silver-800'
                      }`}
                    >
                      Select Divisions to Import
                    </p>
                  </div>

                  <div className="divide-y divide-fsgu-gray-700">
                    {importParsedData.sheets.map((sheet) => (
                      <div key={sheet.name}>
                        <div
                          className={`flex items-center gap-3 px-4 py-3 cursor-pointer ${
                            isDarkMode ? 'hover:bg-fsgu-gray-700/50' : 'hover:bg-fsgu-silver-50'
                          }`}
                          onClick={() =>
                            setExpandedSheet(expandedSheet === sheet.name ? null : sheet.name)
                          }
                        >
                          <input
                            type="checkbox"
                            checked={selectedSheets[sheet.name] || false}
                            onChange={(e) => {
                              e.stopPropagation();
                              toggleSheetSelection(sheet.name);
                            }}
                            className="w-4 h-4 rounded border-fsgu-gray-500"
                          />

                          <div className="flex-1">
                            <p
                              className={`font-medium ${
                                isDarkMode ? 'text-fsgu-silver-200' : 'text-fsgu-silver-800'
                              }`}
                            >
                              {sheet.name}
                            </p>
                            <div
                              className={`flex items-center gap-4 text-xs ${
                                isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                              }`}
                            >
                              <span className="flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                {sheet.summary.players} players
                              </span>
                              <span className="flex items-center gap-1">
                                <Hash className="w-3 h-3" />
                                {sheet.summary.events} events
                              </span>
                              {sheet.summary.bonusColumns > 0 && (
                                <span className="flex items-center gap-1">
                                  <Trophy className="w-3 h-3" />
                                  {sheet.summary.bonusColumns} bonus
                                </span>
                              )}
                            </div>
                          </div>

                          {expandedSheet === sheet.name ? (
                            <ChevronDown
                              className={`w-4 h-4 ${
                                isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                              }`}
                            />
                          ) : (
                            <ChevronRight
                              className={`w-4 h-4 ${
                                isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                              }`}
                            />
                          )}
                        </div>

                        {expandedSheet === sheet.name && (
                          <div
                            className={`px-4 py-3 ${
                              isDarkMode ? 'bg-fsgu-gray-900' : 'bg-fsgu-silver-50'
                            }`}
                          >
                            <p
                              className={`text-xs font-medium mb-2 ${
                                isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                              }`}
                            >
                              Preview (first 5 players):
                            </p>
                            <div className="overflow-x-auto">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr
                                    className={
                                      isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                                    }
                                  >
                                    <th className="text-left py-1 pr-4">#</th>
                                    <th className="text-left py-1 pr-4">Player</th>
                                    <th className="text-right py-1 pr-4">OOM Total</th>
                                    <th className="text-right py-1">Best 10</th>
                                  </tr>
                                </thead>
                                <tbody
                                  className={
                                    isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                                  }
                                >
                                  {sheet.rows.slice(0, 5).map((row, i) => (
                                    <tr key={i}>
                                      <td className="py-1 pr-4">{row.position}</td>
                                      <td className="py-1 pr-4">{row.player_name}</td>
                                      <td
                                        className={`py-1 pr-4 text-right font-medium ${
                                          isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-600'
                                        }`}
                                      >
                                        {row.oom_total_pts ?? '-'}
                                      </td>
                                      <td className="py-1 text-right">
                                        {row.best_10_pts ?? '-'}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                            {sheet.rows.length > 5 && (
                              <p
                                className={`text-xs mt-2 ${
                                  isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                                }`}
                              >
                                ...and {sheet.rows.length - 5} more players
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {importParsedData && Object.values(selectedSheets).some((v) => v) && (
                <div
                  className={`p-3 rounded-lg text-sm ${
                    isDarkMode ? 'bg-amber-500/10 text-amber-400' : 'bg-amber-500/10 text-amber-600'
                  }`}
                >
                  <AlertCircle className="w-4 h-4 inline mr-2" />
                  This will replace existing OOM data for selected divisions in {importYear}
                </div>
              )}
            </div>

            <div
              className={`flex justify-between items-center gap-3 px-6 py-4 border-t flex-shrink-0 ${
                isDarkMode ? 'border-fsgu-gray-700' : 'border-fsgu-silver-200'
              }`}
            >
              <div
                className={`text-sm ${isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'}`}
              >
                {importParsedData && (
                  <>
                    {Object.values(selectedSheets).filter((v) => v).length} of{' '}
                    {importParsedData.sheets.length} divisions selected
                  </>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={closeImportModal}
                  className={`px-4 py-2 rounded-lg font-medium ${
                    isDarkMode
                      ? 'bg-fsgu-gray-700 text-fsgu-silver-300 hover:bg-fsgu-gray-600'
                      : 'bg-fsgu-silver-100 text-fsgu-silver-700 hover:bg-fsgu-silver-200'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleImport}
                  disabled={
                    !importParsedData || !Object.values(selectedSheets).some((v) => v) || importing
                  }
                  className="btn btn-primary disabled:opacity-50"
                >
                  {importing ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Import Selected
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOOM;